/**
 * Deterministic avatar fallback color: hashes a display name to a stable hue
 * and returns an oklch() color that keeps good contrast against white
 * initials in both light and dark themes (fixed mid lightness + moderate
 * chroma, only the hue varies). Same name in → same color out, across
 * sessions and devices.
 */

/** FNV-1a 32-bit — tiny, stable, good distribution for short strings. */
function hashName(name: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) {
    hash ^= name.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Stable background color for a name. Normalizes case/whitespace so
 * "Suman Khadka" and " suman khadka " collide on purpose. Pair with white
 * initials (`AVATAR_INITIALS_COLOR`).
 */
export function nameToColor(name: string): string {
  const normalized = name.trim().toLowerCase();
  const hue = hashName(normalized) % 360;
  // L 0.55 / C 0.13: dark enough for white text (≥ ~4:1 across all hues),
  // saturated enough to read as a color chip on both themes.
  return `oklch(0.55 0.13 ${hue})`;
}

/** Foreground to use on top of nameToColor() backgrounds. */
export const AVATAR_INITIALS_COLOR = "oklch(0.985 0 0)";

/** Up-to-two-letter initials for a display name ("Suman Khadka" → "SK"). */
export function nameToInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
